import { Badge, Button, Flex, Space, Table, Tag, Tooltip, Typography } from 'antd'
import { type BaseType } from 'antd/es/typography/Base'

import {
    ArrowFatDownIcon,
    ArrowFatRightIcon,
    ArrowFatUpIcon,
    ArrowRightIcon,
    Bug,
    Certificate,
    NoteIcon,
    ShieldCheck,
    X,
    ShieldIcon
} from '@phosphor-icons/react'

import { type Product } from '../../../../../types'

import { type SeatsComparison, type BatchRow } from './useBatch'

type BatchTableProps = {
    rows: BatchRow[]
    fields: string[]
    onOverrideVerification: (i: number) => void
}

export const BatchTable = ({ rows, fields, onOverrideVerification }: BatchTableProps) => {
    const columns = [
        Table.EXPAND_COLUMN,
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: BatchRow['status'], _record: BatchRow) => {
                return (
                    <>
                        {status === 'DRAFT' && (
                            <Space>
                                <ShieldIcon
                                    size={24}
                                    alt="Row is Valid, Must Verify the School Information with the Databse"
                                />
                                <Badge>Valid</Badge>
                            </Space>
                        )}
                        {status === 'INVALID' && (
                            <Space>
                                <Bug color="red" size={24} alt="Row can not be processed, check messages" />{' '}
                                <Badge>Invalid</Badge>
                            </Space>
                        )}
                        {status === 'VERIFICATION_FAILED' && (
                            <Space>
                                <Bug color="red" size={24} alt="Row can not be processed, check messages" />{' '}
                                <Badge>Verification Failed</Badge>
                            </Space>
                        )}
                        {status === 'COMPLETE' && (
                            <Space>
                                <Certificate color="green" size={24} alt="Row has been processed" />
                                <Badge>Complete</Badge>
                            </Space>
                        )}
                        {status === 'FAILED' && (
                            <Space>
                                <X color="red" size={24} alt="Row failed to process" />
                                <Badge>Failed</Badge>
                            </Space>
                        )}
                        {status === 'READY_TO_PROCESS' && (
                            <Space>
                                {_record.errors.length > 0 && <NoteIcon color="red" size={24} />}
                                <ShieldCheck size={24} alt="Ready to Process" /> <Badge>Verified</Badge>
                            </Space>
                        )}
                    </>
                )
            }
        },
        {
            title: 'School Name',
            dataIndex: ['values', 'schoolName'],
            key: 'schoolName'
        },
        {
            title: 'Seats',
            dataIndex: ['values', 'seats'],
            key: 'seats'
        },
        {
            title: 'Admin Email',
            dataIndex: ['values', 'adminEmails'],
            key: 'adminEmails',
            render: (adminEmails: string[], record: BatchRow) => (
                <>
                    {adminEmails.map(email => (
                        <div key={`${record.schoolId}${email}`}>{email}</div>
                    ))}
                </>
            )
        },
        {
            title: 'Products',
            dataIndex: ['values', 'products'],
            key: 'products',
            render: (products: Product[], record: BatchRow) => (
                <>{record.isValid && products.map(product => <Tag key={product.key}>{product.key}</Tag>)}</>
            )
        },
        {
            title: 'Action',

            dataIndex: 'seatsComparison',
            key: 'seatsComparison',
            render: (seatsComparison: SeatsComparison, record: BatchRow) => {
                let type: BaseType | undefined = undefined
                if (seatsComparison) {
                    if (seatsComparison.old < seatsComparison.new) {
                        type = 'success'
                    } else if (seatsComparison.old > seatsComparison.new) {
                        type = 'danger'
                    }
                }

                return (
                    <Flex>
                        {seatsComparison && (
                            <Flex align="center" justify="space-between">
                                {seatsComparison.old < seatsComparison.new && (
                                    <Tooltip title="Seats have gone up since last subscription">
                                        <ArrowFatUpIcon size={24} />
                                    </Tooltip>
                                )}
                                {seatsComparison.old > seatsComparison.new && (
                                    <Tooltip title="Seats have gone down since last subscription. Some users may lose their seat">
                                        <ArrowFatDownIcon size={24} />
                                    </Tooltip>
                                )}
                                {seatsComparison.old === seatsComparison.new && (
                                    <Tooltip title="Seats have have not changed since last subscriptiont">
                                        <ArrowFatRightIcon size={24} />
                                    </Tooltip>
                                )}
                                <Space size={4} />
                                <Flex align="center">
                                    {seatsComparison.old} <ArrowRightIcon />
                                    <Typography.Text type={type}>{seatsComparison.new}</Typography.Text>
                                </Flex>
                            </Flex>
                        )}

                        {record.actionType === 'NewSchool' && (
                            <Tooltip title="A new school will be created">New</Tooltip>
                        )}
                    </Flex>
                )
            }
        }
    ]

    // add any additional fields
    if (fields.find(field => field === 'postalCode')) {
        columns.splice(5, 0, {
            title: 'Postal Code',
            dataIndex: ['values', 'postalCode'],
            key: 'postalCode'
        })
    }

    // add any additional fields
    if (fields.find(field => field === 'domain')) {
        columns.splice(5, 0, {
            title: 'Domain',
            dataIndex: ['values', 'domain'],
            key: 'domain'
        })
    }

    const expandedRowRender = (record: BatchRow, i: number) => {
        return (
            <>
                {record.errors.map(error => (
                    <p key={error.message}>{error.message}</p>
                ))}
                {record.status === 'VERIFICATION_FAILED' && record.errors.length > 0 && (
                    <Button onClick={() => onOverrideVerification(i)}>Override Verification Check</Button>
                )}
            </>
        )
    }

    return (
        <Table
            dataSource={rows}
            columns={columns}
            pagination={false}
            rowClassName={record => (!record.isValid ? 'error-row' : '')}
            expandable={{
                expandedRowRender,
                defaultExpandedRowKeys: ['0']
            }}
        />
    )
}
